import React, { ComponentProps } from 'react'

import { Tooltip } from '@mui/material'

import { Icon } from '../Icon/Icon'
import { StyledTag } from './StyledTag'

export type TagType = 'neutral' | 'success' | 'warning' | 'info' | 'error' | 'special' | 'transparent' | 'glass-blur' | 'primary' | 'accent'
export type TagSize = 'mini' | 'small' | 'medium' | 'large'

type PreconfiguredTag = {
  type: TagType
  text: string
}

const preconfiguredTags: Record<string, PreconfiguredTag> = {
  'pre-order': { type: 'info', text: 'Pre Order' },
  'pre-owned': { type: 'special', text: 'Pre Owned' },
  'brand-new': { type: 'success', text: 'Brand New' },
  'recently-added': { type: 'info', text: 'Recently Added' },
  'box-damage': { type: 'warning', text: 'Box Damage' },
  'just-arrived': { type: 'success', text: 'Just Arrived' },
  'early-bird': { type: 'special', text: 'Early Bird' },
  'featured': { type: 'special', text: 'Featured' },
}

export type TagProps = Omit<ComponentProps<typeof StyledTag>, 'type' | '$type' | '$size' | 'endIcon' | 'icon' | 'label' | 'size'> & {
  label: string
  icon?: ComponentProps<typeof Icon>['icon']
  endIcon?: ComponentProps<typeof Icon>['icon']
  href?: string
  type?: TagType
  size?: TagSize
  glassEffect?: boolean
  maxLength?: number
  showTooltip?: boolean
}

export const Tag: React.FC<TagProps> = ({
  type = 'neutral',
  glassEffect,
  label,
  icon,
  endIcon,
  href,
  onClick,
  size = 'medium',
  maxLength = 20,
  showTooltip = true,
  ...props
}) => {
  if (!label) return null

  const tagType = label in preconfiguredTags
    ? preconfiguredTags[label].type
    : type

  const displayText = label in preconfiguredTags
    ? preconfiguredTags[label].text
    : label

  const truncatedText = displayText.length > maxLength
    ? `${displayText.substring(0, maxLength)}...`
    : displayText

  const needsTruncation = displayText.length > maxLength

  const tag = href ? (
    <StyledTag
      $type={tagType}
      $size={size}
      $glassEffect={glassEffect}
      $disabled={!href && !onClick}
      disableRipple
      href={href}
      {...props}
    >
      {icon && <Icon icon={icon} />}
      {truncatedText}
      {endIcon && <Icon icon={endIcon} sx={{ ml: 0.5 }} />}
    </StyledTag>
  ) : (
    <StyledTag
      $type={tagType}
      $size={size}
      $glassEffect={glassEffect}
      $disabled={!href && !onClick}
      disableRipple
      onClick={onClick}
      {...props}
    >
      {icon && <Icon icon={icon} />}
      {truncatedText}
      {endIcon && <Icon icon={endIcon} sx={{ ml: 0.5 }} />}
    </StyledTag>
  )

  if (needsTruncation && showTooltip) {
    return (
      <Tooltip
        title={displayText}
        disableInteractive={true}
        arrow
      >
        {tag}
      </Tooltip>
    )
  }

  return tag
}